import { useState, useCallback } from 'react';

/**
 * A React hook for managing the state of a multi-step popup wizard.
 * It provides functionality to navigate through different steps of the wizard.
 *
 * @param {string[]} steps - String steps of the wizard
 * @returns {{
 *   currentStep: string | null,
 *   onSetStep: (step: string) => void
 * }} An object containing:
 *   - currentStep (string | null): The current active step of the wizard.
 *   - onSetStep (function): A function that sets specific step.
 *
 * @example
 * // In a component
 * const { currentStep, onSetStep } = usePopupWizard(['location', 'date', 'guests']);
 * // Use `currentStep` to render the current step content,
 * // `onSetStep` to navigate to a specific step
 */
export function usePopupWizard(steps) {
  const [currentStep, setCurrentStep] = useState(null);

  const setSpecificStep = useCallback(
    (step = null) => {
      if (step === null) {
        setCurrentStep(null);
        return;
      }
      const stepIdx = steps.indexOf(step);
      if (stepIdx >= 0) {
        setCurrentStep(stepIdx);
      }
    },
    [steps, setCurrentStep],
  );

  return {
    currentStep: currentStep === null ? null : steps[currentStep],
    onSetStep: setSpecificStep,
    onReset: () => setSpecificStep(null),
  };
}
